import { useEffect, useMemo, useState } from 'react'
import { supabase } from '../../lib/supabase'
import { allocateInvoice, COST_TYPE_LABEL, CostType, SplitMethod } from '../../lib/commission'
import {
  COST_CATEGORIES,
  GROUP_LABEL,
  apportionGst,
  categoryOf,
  checkGst,
  gstOn,
} from '../../lib/costCategories'
import { reconcileArrival } from '../../services/standardCostService'
import { ACCRUAL_SOURCES } from '../../lib/estimateSource'
import { isValidAbn } from '../../lib/abn'
import { toast } from '../Toast'

/**
 * One supplier invoice, split across the vehicles it covers.
 *
 * Winnie, v4 of the change register: "How to apply the bill to the car (Team Inspect,
 * Tiempo Transport etc.) For example, this invoice for 3 cars, the seller needs to split
 * exact $489 to each car."
 *
 * Until now an expense belonged to one deal, so a three-car transport bill had to be typed
 * as three separate expenses with nothing tying them to the invoice and nothing checking
 * the parts added up. The register's Cost of Sales is supposed to be "all
 * acquisition/direct costs captured from the invoices per vehicle VIN" - it cannot be, if
 * the invoice cannot be entered as an invoice.
 *
 * Four things changed in this version, all from Winnie's 7 Sep list:
 *
 *   - Vehicles are picked from a searchable multi-select rather than one dropdown per row.
 *     Choosing three cars out of 155 through three separate dropdowns is the kind of task
 *     people quietly stop doing.
 *   - The split can be Equal, Weighted or Manual. Equal and Weighted are computed, with
 *     the rounding remainder landing on the last line so the parts add back exactly -
 *     $100 across three cars is 33.33, 33.33, 33.34, not three lines that lose a cent.
 *   - GST is computed from the invoice total rather than typed as an unrelated number.
 *     That was the defect: the old form accepted $489 and $48.90 side by side, and $48.90
 *     is ten per cent added ON TOP. A GST-inclusive invoice of $489 contains $44.45.
 *   - The category now says whether it is an acquisition cost or a direct cost, because
 *     that is the split Winnie asked for and it is a property of the category, not a
 *     second thing to remember.
 *
 * What has not changed is the rule that matters: it will not save until the lines add up
 * to the invoice, to the cent.
 */

interface DealOption {
  id: string
  label: string
  vin: string
  search: string
}

interface Line {
  /* Its own identity. Winnie, 11 Sep section 4: one invoice carries several line items,
     and two of them can be the same car - freight in and a detail charge on the one
     vehicle - so a line cannot be identified by the vehicle it names. */
  key: string
  dealId: string
  /* Which side of the sale, and the cost type, per line rather than per invoice. Empty
     means "whatever the invoice says", so changing the invoice's answer still moves every
     line that has not been given one of its own. */
  category: string
  costType: CostType | null
  amount: string
  weight: string
  note: string
}

const money = (n: number) =>
  `$${n.toLocaleString('en-AU', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`

const cents = (v: string | number) => Math.round((Number(v) || 0) * 100)

let lineSeq = 0
const newLine = (dealId: string, amount = '', note = ''): Line => ({
  key: 'line-' + ++lineSeq,
  dealId,
  category: '',
  costType: null,
  amount,
  weight: '1',
  note,
})

export interface SplitPrefill {
  supplier?: string
  /** Digits only, read off the invoice and checksum-validated. '' when none was found. */
  supplierAbn?: string
  invoiceRef?: string
  incurredOn?: string
  category?: string
  total?: string
  gstTotal?: string
  lines?: { dealId: string; amount: string; note: string }[]
  supplierInvoiceId?: string | null
}

export default function SplitInvoiceForm({
  userId,
  onSaved,
  onCancel,
  prefill,
}: {
  userId?: string | null
  onSaved: () => void
  onCancel: () => void
  prefill?: SplitPrefill
}) {
  const [deals, setDeals] = useState<DealOption[]>([])
  const [supplier, setSupplier] = useState((prefill && prefill.supplier) || '')
  const [supplierAbn, setSupplierAbn] = useState((prefill && prefill.supplierAbn) || '')
  const [invoiceRef, setInvoiceRef] = useState((prefill && prefill.invoiceRef) || '')
  const [incurredOn, setIncurredOn] = useState(
    (prefill && prefill.incurredOn) || new Date().toISOString().slice(0, 10),
  )
  const [category, setCategory] = useState((prefill && prefill.category) || COST_CATEGORIES[0].value)
  const [costType, setCostType] = useState<CostType | null>(null)
  const [total, setTotal] = useState((prefill && prefill.total) || '')
  const [gstTotal, setGstTotal] = useState((prefill && prefill.gstTotal) || '')
  const [gstTouched, setGstTouched] = useState(Boolean(prefill && prefill.gstTotal))
  const [capitalised, setCapitalised] = useState(true)
  const [method, setMethod] = useState<SplitMethod>('Equal')
  const [search, setSearch] = useState('')
  const [lines, setLines] = useState<Line[]>(
    prefill && prefill.lines && prefill.lines.length
      ? prefill.lines.map((l) => newLine(l.dealId, l.amount, l.note))
      : [],
  )
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [duplicateOf, setDuplicateOf] = useState<string | null>(null)

  useEffect(() => {
    supabase
      .from('deals')
      .select(
        'id, stock_number, autograb_vin, vehicle_vin, vehicle_year, vehicle_make, vehicle_model, autograb_year, autograb_make, autograb_model',
      )
      .is('deleted_at', null)
      .order('created_at', { ascending: true })
      .then(({ data }) => {
        setDeals(
          ((data || []) as Record<string, unknown>[]).map((d) => {
            const vin = String(d.autograb_vin || d.vehicle_vin || '')
            const label = [
              d.stock_number ? `#${d.stock_number}` : null,
              [d.autograb_year ?? d.vehicle_year, d.autograb_make || d.vehicle_make, d.autograb_model || d.vehicle_model]
                .filter(Boolean)
                .join(' '),
              vin || null,
            ]
              .filter(Boolean)
              .join(' · ')
            return { id: String(d.id), label, vin, search: label.toLowerCase() }
          }),
        )
      })
  }, [])

  /*
   * A supplier invoice number that already exists against the same supplier is worth
   * naming before the second copy is saved, not after. It is a warning rather than a
   * block: a supplier can legitimately re-use a reference across months, and a person
   * looking at the invoice can tell in a second.
   */
  useEffect(() => {
    const ref = invoiceRef.trim()
    if (!ref) {
      setDuplicateOf(null)
      return
    }
    let live = true
    const t = window.setTimeout(() => {
      supabase
        .from('expenses')
        .select('id, supplier, incurred_on')
        .eq('invoice_ref', ref)
        .limit(1)
        .then(({ data }) => {
          if (!live) return
          const hit = (data || [])[0] as { supplier?: string; incurred_on?: string } | undefined
          setDuplicateOf(
            hit ? `${hit.supplier || 'a supplier'}${hit.incurred_on ? `, ${hit.incurred_on}` : ''}` : null,
          )
        })
    }, 400)
    return () => {
      live = false
      window.clearTimeout(t)
    }
  }, [invoiceRef])

  const cat = categoryOf(category)
  const invoiceCents = cents(total)

  /*
   * The cost type follows the category where the category settles it, and has to be
   * chosen where it does not. Transport is the case that matters: freight in and freight
   * out sit on opposite sides of the margin, and a cost filed on the wrong side moves
   * a commission without anyone noticing. So the form asks rather than guesses, and will
   * not save until it has an answer.
   */
  useEffect(() => {
    setCostType(cat ? cat.costType : null)
  }, [category, cat])

  const CHOOSABLE: CostType[] = ['FreightIn', 'FreightOut', 'OtherOutflow', 'Overhead']

  /* GST follows the total until somebody overrides it, and then stops moving. */
  useEffect(() => {
    if (gstTouched) return
    setGstTotal(invoiceCents > 0 && cat && cat.usuallyHasGst ? String(gstOn(invoiceCents / 100)) : '')
  }, [invoiceCents, category, gstTouched, cat])

  const gstVerdict = useMemo(
    () => checkGst(invoiceCents / 100, Number(gstTotal) || 0, category),
    [invoiceCents, gstTotal, category],
  )

  /* Every line counts towards the total, including one whose vehicle has not been picked
     yet. Dropping those would let a half-finished line look as if the invoice balanced. */
  const chosen = lines

  /* A line's own answer, falling back to the invoice's. */
  const catOf = (l: Line) => categoryOf(l.category || category)
  const costTypeOf = (l: Line): CostType | null => {
    if (l.costType) return l.costType
    const c = catOf(l)
    return c ? c.costType : null
  }
  const needVehicle = lines.filter((l) => !l.dealId).length
  const needCostType = lines.filter((l) => !costTypeOf(l)).length

  /* Equal and Weighted are computed from the invoice; Manual is whatever was typed. */
  const computed = useMemo(() => {
    if (method === 'Manual' || !chosen.length || invoiceCents <= 0) return null
    return allocateInvoice({
      invoiceTotal: invoiceCents / 100,
      vins: chosen.map((l) => l.key),
      method,
      weights: Object.fromEntries(chosen.map((l) => [l.key, Number(l.weight) || 0])),
    })
  }, [method, chosen, invoiceCents])

  const amountFor = (l: Line): number => {
    if (method === 'Manual') return (Number(l.amount) || 0)
    if (!computed || !computed.ok) {
      const hit = computed && computed.allocations.find((a) => a.vin === l.key)
      return hit ? hit.allocated_amount : 0
    }
    const hit = computed.allocations.find((a) => a.vin === l.key)
    return hit ? hit.allocated_amount : 0
  }

  const allocated = chosen.reduce((s, l) => s + cents(amountFor(l)), 0)
  const remaining = invoiceCents - allocated
  /* An invoice with no total is not a balanced invoice, it is an empty one. Without the
     total check a fresh form reads "Balanced" before anything has been typed into it. */
  const balanced = invoiceCents > 0 && remaining === 0 && chosen.length > 0

  const toggleDeal = (id: string) =>
    setLines((ls) =>
      ls.some((l) => l.dealId === id)
        ? ls.filter((l) => l.dealId !== id)
        : [...ls, newLine(id)],
    )

  /* Winnie, 11 Sep section 4: "+ Add line item". The vehicle is chosen on the line, so a
     line can be started before anyone knows which car it is for - and a cost can be
     entered by hand with no invoice scanned at all, which is how most of them arrive. */
  const addLine = () => setLines((ls) => [...ls, newLine('')])

  const removeLine = (key: string) => setLines((ls) => ls.filter((l) => l.key !== key))

  const setLine = (key: string, patch: Partial<Line>) =>
    setLines((ls) => ls.map((l) => (l.key === key ? { ...l, ...patch } : l)))

  const visible = useMemo(() => {
    const t = search.trim().toLowerCase()
    const list = t ? deals.filter((d) => d.search.includes(t)) : deals
    return list.slice(0, 60)
  }, [deals, search])

  const save = async () => {
    setBusy(true)
    setError(null)
    /* Digits only, and only when it passes the checksum - the same rule the reader applies.
       A typed-over ABN that fails is dropped rather than stored wrong. */
    const typed = supplierAbn.replace(/\D/g, '')
    const abnDigits = typed && isValidAbn(typed) ? typed : ''
    const amounts = chosen.map((l) => amountFor(l))
    const gstShares = apportionGst(Number(gstTotal) || 0, amounts)

    /* Spec v2 section 5: where a VIN already carries an estimated accrual for this cost type,
       the allocation REPLACES it. It used to sit beside it, which double-counted the cost -
       the $150 estimate and the $163 actual both landing in landed cost - and left the accrual
       to never reverse. That is the spec's own worked example (VIN003), so the headline case
       was the broken one. reconcileArrival updates the estimate in place and posts the
       reversal; per section 3 it does not touch the commission record. */
    const { data: openEstimates } = await supabase
      .from('expenses')
      .select('id, deal_id, cost_type')
      .in('deal_id', chosen.map((l) => l.dealId))
      .in('cost_type', [...new Set(chosen.map((l) => costTypeOf(l)).filter(Boolean))] as CostType[])
      .eq('bill_status', 'Estimated')
      /* Both kinds of estimate, not just the rate-card one. A freight figure a super admin
         set by hand is exactly the thing this invoice is arriving to replace; matching only
         'standard_cost' would have left it sitting beside the invoice and the vehicle would
         have carried the freight twice. */
      .in('estimate_source', ACCRUAL_SOURCES as unknown as string[])
      .is('estimate_reconciled_at', null)

    /* Keyed by vehicle AND cost type now that one invoice can carry a freight line and a
       reconditioning line for the same car: an estimate only answers to its own type. */
    const estimateFor: Record<string, string> = {}
    for (const e of (openEstimates || []) as { id: string; deal_id: string; cost_type: string }[]) {
      estimateFor[e.deal_id + '|' + e.cost_type] = e.id
    }

    const replaced = new Set<number>()
    for (let i = 0; i < chosen.length; i++) {
      const estKey = chosen[i].dealId + '|' + costTypeOf(chosen[i])
      const estId = estimateFor[estKey]
      if (!estId) continue
      const r = await reconcileArrival(estId, amounts[i], { supplier, invoiceRef })
      if (!r.ok) continue
      replaced.add(i)
      /* One estimate answers one line. Without this, two lines for the same car and cost
         type would both reconcile against it and the second amount would be lost. */
      delete estimateFor[estKey]
      /* reconcileArrival is a P&L concern and knows nothing about this invoice, so the
         allocation's own fields are written on afterwards - the audit trail section 5 asks
         for is the invoice reference reaching every VIN it was split across. */
      await supabase
        .from('expenses')
        .update({
          gst_amount: gstShares[i],
          supplier_invoice_id: (prefill && prefill.supplierInvoiceId) || null,
          category: chosen[i].category || category,
          incurred_on: incurredOn,
          capitalised,
          supplier_abn: abnDigits || null,
          /* The estimate is being replaced by a real document, so it stops being an
             estimate in every sense - including the tax invoice tick. */
          tax_invoice: true,
          description: chosen[i].note || `${supplier} ${invoiceRef}`.trim(),
        })
        .eq('id', estId)
    }

    const rows: Record<string, unknown>[] = []
    chosen.forEach((l, i) => {
      if (replaced.has(i)) return
      rows.push({
        deal_id: l.dealId,
        supplier_invoice_id: (prefill && prefill.supplierInvoiceId) || null,
        category: l.category || category,
        description: l.note || `${supplier} ${invoiceRef}`.trim(),
        amount: amounts[i],
        gst_amount: gstShares[i],
        supplier: supplier || null,
        /* The screen reads paid_to first and falls back to supplier, so a row saved from
           here used to show the supplier only by that fallback. Writing both keeps an
           uploaded cost and a hand-entered one the same shape. */
        paid_to: supplier || null,
        supplier_abn: abnDigits || null,
        /* Winnie, 13 Sep: a cost line created from an uploaded invoice defaults the Tax
           Invoice tick to checked - "the presence of an uploaded document is itself the
           evidence being provided". A default, not a lock: the row can be unticked on the
           Cost of Sales screen if the file turns out to be a delivery docket. */
        tax_invoice: true,
        invoice_ref: invoiceRef || null,
        incurred_on: incurredOn,
        capitalised,
        cost_type: costTypeOf(l),
        created_by: userId || null,
      })
    })

    const { data: made, error: err } = rows.length
      ? await supabase.from('expenses').insert(rows).select('id')
      : { data: [], error: null }
    if (err) {
      setBusy(false)
      setError(err.message)
      return
    }

    /* Winnie, 11 Sep section 3: every cost entry leaves a trail. The edit form logs its
       own; these rows are inserted straight from here, so they log theirs. Non-blocking -
       an audit row that fails to write must not lose the costs it was recording. */
    try {
      const trail = ((made || []) as { id: string }[]).map((row, n) => ({
        actor_user_id: userId || null,
        action: 'expense_created',
        target_name: row.id,
        details: {
          dealId: rows[n] ? rows[n].deal_id : null,
          amount: rows[n] ? rows[n].amount : null,
          supplierInvoice: invoiceRef || null,
          via: 'split_invoice',
        },
        created_at: new Date().toISOString(),
      }))
      if (trail.length) await supabase.from('audit_log').insert(trail)
    } catch (e) {
      console.error('Could not write the audit entries for this invoice (non-blocking)', e)
    }

    setBusy(false)
    toast.saved()
    onSaved()
  }

  return (
    <div className="rep-card" style={{ padding: 16 }}>
      <h3 style={{ marginTop: 0 }}>Split an invoice across vehicles</h3>
      <p className="rep-muted" style={{ marginTop: 0 }}>
        Enter the invoice once, pick the vehicles it covers, and choose how it splits. It will
        not save until the lines add up to the invoice total.
      </p>

      <div className="rep-toolbar" style={{ flexWrap: 'wrap' }}>
        <label className="rep-month-label">Supplier</label>
        <input className="rep-select" value={supplier} onChange={(e) => setSupplier(e.target.value)} placeholder="Tiempo Transport Pty Ltd" />
        {/* The ABN belongs to the invoice, so every line split off it inherits the same one.
            Auto-filled from the document where it could be read; still editable. */}
        <label className="rep-month-label">Supplier ABN</label>
        <input className="rep-select" value={supplierAbn} onChange={(e) => setSupplierAbn(e.target.value)} placeholder="11 digits" />
        <label className="rep-month-label">Invoice ref</label>
        <input className="rep-select" value={invoiceRef} onChange={(e) => setInvoiceRef(e.target.value)} placeholder="INV-8087" />
        <label className="rep-month-label">Date</label>
        <input className="rep-select" type="date" value={incurredOn} onChange={(e) => setIncurredOn(e.target.value)} />
        <label className="rep-month-label">Category</label>
        <select className="rep-select" value={category} onChange={(e) => setCategory(e.target.value)}>
          {COST_CATEGORIES.map((c) => (
            <option key={c.value} value={c.value}>{c.label}</option>
          ))}
        </select>
        {cat && <span className="split-group">{GROUP_LABEL[cat.group]}</span>}
        {cat && cat.costType === null && (
          <>
            <label className="rep-month-label">Cost type</label>
            <select
              className="rep-select"
              value={costType || ''}
              onChange={(e) => setCostType((e.target.value || null) as CostType | null)}
            >
              <option value="">Which side of the sale?</option>
              {CHOOSABLE.map((c) => (
                <option key={c} value={c}>{COST_TYPE_LABEL[c]}</option>
              ))}
            </select>
          </>
        )}
      </div>

      {duplicateOf && (
        <div className="split-note warn">
          Invoice <b>{invoiceRef}</b> is already recorded against {duplicateOf}. Worth checking
          this is not the same bill twice.
        </div>
      )}

      <div className="rep-toolbar" style={{ flexWrap: 'wrap' }}>
        <label className="rep-month-label">Invoice total (incl GST)</label>
        <input className="rep-select" inputMode="decimal" value={total} onChange={(e) => setTotal(e.target.value)} placeholder="489.00" />
        <label className="rep-month-label">GST</label>
        <input
          className="rep-select"
          inputMode="decimal"
          value={gstTotal}
          onChange={(e) => { setGstTouched(true); setGstTotal(e.target.value) }}
          placeholder="44.45"
        />
        {gstTouched && (
          <button className="rep-btn" onClick={() => setGstTouched(false)} title="Go back to the calculated GST">
            Recalculate
          </button>
        )}
        <label className="rep-month-label">
          <input type="checkbox" checked={capitalised} onChange={(e) => setCapitalised(e.target.checked)} /> Capitalised
        </label>
      </div>

      {gstVerdict.message && (
        <div className={`split-note ${gstVerdict.ok ? 'warn' : 'bad'}`}>{gstVerdict.message}</div>
      )}

      <div className="rep-toolbar" style={{ flexWrap: 'wrap' }}>
        <label className="rep-month-label">Split</label>
        {(['Equal', 'Weighted', 'Manual'] as SplitMethod[]).map((m) => (
          <button
            key={m}
            className={`rep-btn ${method === m ? 'primary' : ''}`}
            onClick={() => setMethod(m)}
          >
            {m}
          </button>
        ))}
        <span className="rep-muted">
          {method === 'Equal'
            ? 'Divided evenly; the rounding remainder goes on the last line.'
            : method === 'Weighted'
              ? 'Give each vehicle a weight — distance, hours, whatever the invoice was priced on.'
              : 'Type each amount exactly as the supplier itemised it.'}
        </span>
      </div>

      <div className="split-picker">
        <input
          className="rep-select"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder={`Search ${deals.length} vehicles by stock number, make or VIN…`}
        />
        <div className="split-list">
          {visible.length === 0 ? (
            <div className="rep-muted" style={{ padding: 8 }}>No vehicles match.</div>
          ) : (
            visible.map((d) => (
              <label key={d.id} className={`split-item ${lines.some((l) => l.dealId === d.id) ? 'on' : ''}`}>
                <input
                  type="checkbox"
                  checked={lines.some((l) => l.dealId === d.id)}
                  onChange={() => toggleDeal(d.id)}
                />
                <span>{d.label}</span>
              </label>
            ))
          )}
        </div>
      </div>

      {chosen.length > 0 && (
        <table className="rep-table" style={{ width: '100%', marginTop: 12 }}>
          <thead>
            <tr>
              <th>Vehicle</th>
                <th>Which side of the sale</th>
                <th>Cost type</th>
              {method === 'Weighted' && <th className="num">Weight</th>}
              <th className="num">Amount</th>
              <th className="num">GST</th>
              <th>Line note</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {chosen.map((l, i) => {
              const amt = amountFor(l)
              const shares = apportionGst(Number(gstTotal) || 0, chosen.map((x) => amountFor(x)))
              return (
                <tr key={l.key}>
                  <td>
                      {/* On the line, so a line pre-filled from an invoice that matched the
                          wrong car can be put right here rather than started again. */}
                      <select
                        className="rep-select"
                        value={l.dealId}
                        onChange={(e) => setLine(l.key, { dealId: e.target.value })}
                        style={{ minWidth: 200 }}
                      >
                        <option value="">Choose the vehicle…</option>
                        {deals.map((d) => (
                          <option key={d.id} value={d.id}>{d.label}</option>
                        ))}
                      </select>
                    </td>
                    <td>
                      <select
                        className="rep-select"
                        value={l.category || category}
                        onChange={(e) => setLine(l.key, { category: e.target.value, costType: null })}
                      >
                        {COST_CATEGORIES.map((c) => (
                          <option key={c.value} value={c.value}>{c.label}</option>
                        ))}
                      </select>
                    </td>
                    <td>
                      <select
                        className="rep-select"
                        value={costTypeOf(l) || ''}
                        onChange={(e) =>
                          setLine(l.key, { costType: (e.target.value || null) as CostType | null })
                        }
                      >
                        <option value="">Choose…</option>
                        {CHOOSABLE.map((t) => (
                          <option key={t} value={t}>{COST_TYPE_LABEL[t]}</option>
                        ))}
                      </select>
                    </td>
                  {method === 'Weighted' && (
                    <td className="num">
                      <input
                        className="rep-select"
                        inputMode="decimal"
                        value={l.weight}
                        onChange={(e) => setLine(l.key, { weight: e.target.value })}
                        style={{ width: 80 }}
                      />
                    </td>
                  )}
                  <td className="num">
                    {method === 'Manual' ? (
                      <input
                        className="rep-select"
                        inputMode="decimal"
                        value={l.amount}
                        onChange={(e) => setLine(l.key, { amount: e.target.value })}
                        style={{ width: 110 }}
                      />
                    ) : (
                      money(amt)
                    )}
                  </td>
                  <td className="num">{money(shares[i] || 0)}</td>
                  <td>
                    <input
                      className="rep-select"
                      value={l.note}
                      onChange={(e) => setLine(l.key, { note: e.target.value })}
                      placeholder="As per invoice line"
                    />
                  </td>
                  <td>
                    <button className="rep-btn" onClick={() => removeLine(l.key)}>Remove</button>
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      )}

      <div className="rep-toolbar" style={{ flexWrap: 'wrap' }}>
        {/* Winnie, 11 Sep section 4: a line item is added here, not by ticking a vehicle
            in a list. The picker above is still the fast way in when the invoice really is
            one charge across several cars. */}
        <button className="rep-btn" onClick={addLine}>+ Add line item</button>
        <span className="rep-muted">
          {chosen.length} line item{chosen.length === 1 ? '' : 's'} · {money(allocated / 100)} of {money(invoiceCents / 100)}
        </span>
        <span className={`split-balance ${balanced ? 'ok' : 'bad'}`}>
          {chosen.length === 0
            ? 'No line items yet'
            : invoiceCents <= 0
            ? 'Enter the invoice total'
            : remaining === 0
            ? 'Balanced'
            : remaining > 0
            ? money(remaining / 100) + ' unallocated'
            : money(-remaining / 100) + ' over the invoice'}
        </span>
        {needVehicle > 0 && (
          <span className="split-balance bad">
            {needVehicle} line item{needVehicle === 1 ? '' : 's'} without a vehicle
          </span>
        )}
        {needCostType > 0 && (
          <span className="split-balance bad">
            {needCostType} line item{needCostType === 1 ? '' : 's'} without a cost type
          </span>
        )}
      </div>

      {error && <div className="split-note bad">{error}</div>}

      <div className="rep-toolbar">
        <button
          className="rep-btn primary"
          onClick={save}
          disabled={!balanced || busy || !gstVerdict.ok || !chosen.length || needVehicle > 0 || needCostType > 0}
        >
          {busy ? 'Saving…' : `Save ${chosen.length} line item${chosen.length === 1 ? '' : 's'}`}
        </button>
        <button className="rep-btn" onClick={onCancel}>Cancel</button>
        {!balanced && chosen.length > 0 && (
          <span className="rep-muted">The line items must add up to the invoice total, to the cent, before this can save.</span>
        )}
      </div>
    </div>
  )
}
